import React, { useState, useEffect } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import SwitchInput from '../../components/SwitchInput/SwitchInput';
import { getNews, getSources, addSource, updateSource, deleteSource } from '../../services/NewsService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';

const COINS = ['BTC', 'ETH', 'SOL', 'ADA', 'LINK', 'DOGE', 'XRP', 'TRX', 'NEAR', 'BNB'];
const POLL_MS = 60000;

function relativeTime(publishedAt, t) {
    const ts = new Date(publishedAt).getTime();
    if (!Number.isFinite(ts)) return '';
    const mins = Math.floor((Date.now() - ts) / 60000);
    if (mins < 1) return t('news.time.justNow');
    if (mins < 60) return `${mins} ${t('news.time.minAgo')}`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} ${t('news.time.hAgo')}`;
    return `${Math.floor(hours / 24)} ${t('news.time.dAgo')}`;
}

function scoreBadge(item, t) {
    if (item.score > 0) return <span className="gh-badge gh-badge-success">{t('news.bullish')} +{item.score}</span>;
    if (item.score < 0) return <span className="gh-badge gh-badge-danger">{t('news.bearish')} −{Math.abs(item.score)}</span>;
    return <span className="gh-badge gh-badge-muted">{t('news.neutral')}</span>;
}

function News() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [news, setNews] = useState([]);
    const [coin, setCoin] = useState('');
    const [sources, setSources] = useState([]);
    const [form, setForm] = useState({ name: '', url: '', type: 'RSS' });
    const [sourceError, setSourceError] = useState('');
    const [notification, setNotification] = useState({});
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        function load() {
            getNews(50, coin || undefined)
                .then(data => { if (!cancelled) setNews(Array.isArray(data) ? data : []); })
                .catch(err => logger.error(err.response ? err.response.data : err.message));
        }
        load();
        const timer = setInterval(load, POLL_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [coin, refreshTick]);

    useEffect(() => {
        refreshSources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function refreshSources() {
        getSources()
            .then(data => setSources(Array.isArray(data) ? data : []))
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }

    function onAddSource(event) {
        event.preventDefault();
        setSourceError('');
        addSource(form.name.trim(), form.url.trim(), form.type)
            .then(() => {
                setForm({ name: '', url: '', type: 'RSS' });
                refreshSources();
            })
            .catch(err => setSourceError(err.response && err.response.data ? String(err.response.data) : t('news.sources.error')));
    }

    function onToggleSource(source, enabled) {
        updateSource(source.id, { enabled })
            .then(updated => setSources(prev => prev.map(s => (s.id === source.id ? { ...s, ...updated } : s))))
            .catch(err => setNotification({ type: 'error', text: err.response ? String(err.response.data) : err.message }));
    }

    function onDeleteSource(source) {
        if (!window.confirm(t('news.sources.confirmDelete'))) return;
        deleteSource(source.id)
            .then(() => setSources(prev => prev.filter(s => s.id !== source.id)))
            .catch(err => setNotification({ type: 'error', text: err.response ? String(err.response.data) : err.message }));
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="py-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                        <h1 className="h4 mb-1">{t('news.title')}</h1>
                        <p className="mb-0 text-muted">{t('news.subtitle')}</p>
                    </div>
                    <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => setRefreshTick(x => x + 1)}>
                        {t('news.refresh')}
                    </button>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                        type="button"
                        className={`btn btn-sm ${coin === '' ? 'btn-gray-800' : 'btn-outline-secondary'}`}
                        onClick={() => setCoin('')}
                    >
                        {t('news.filter.all')}
                    </button>
                    {COINS.map(c => (
                        <button
                            key={c}
                            type="button"
                            className={`btn btn-sm ${coin === c ? 'btn-gray-800' : 'btn-outline-secondary'}`}
                            onClick={() => setCoin(c)}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                <div className="card border-0 shadow mb-4">
                    <div className="card-body">
                        {news.length === 0 && (
                            <p className="small text-muted mb-0">{t('news.empty')}</p>
                        )}
                        {news.map((item, idx) => (
                            <div key={item.id} className={`py-3 ${idx > 0 ? 'border-top border-gray-700' : ''}`}>
                                <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
                                    <div style={{ minWidth: 0 }}>
                                        {item.url ? (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="fw-bold d-block">
                                                {item.title}
                                            </a>
                                        ) : (
                                            <span className="fw-bold d-block">{item.title}</span>
                                        )}
                                        <span className="small text-muted">
                                            {item.sourceName} · {relativeTime(item.publishedAt, t)}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-1 flex-wrap">
                                        {item.veto && (
                                            <span className="gh-badge gh-badge-danger">⚠ {t('news.veto')}</span>
                                        )}
                                        {scoreBadge(item, t)}
                                        {(item.coins || []).map(c => (
                                            <span key={c} className="gh-badge gh-badge-gold">{c}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="card border-0 shadow mb-4">
                    <div className="card-body">
                        <h2 className="h6">{t('news.sources.title')}</h2>
                        <p className="small text-muted">{t('news.sources.hint')}</p>

                        {sources.map(s => (
                            <div key={s.id} className="d-flex align-items-center justify-content-between gap-2 py-2 border-top border-gray-700 flex-wrap">
                                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                    <span
                                        className={`gh-dot ${s.lastError ? 'gh-dot-danger' : 'gh-dot-success'}`}
                                        title={s.lastError || ''}
                                    ></span>
                                    <span className="fw-bold text-truncate">{s.name}</span>
                                    <span className={`gh-badge ${s.type === 'YOUTUBE' ? 'gh-badge-danger' : 'gh-badge-info'}`}>{s.type}</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <SwitchInput
                                        id={`source-enabled-${s.id}`}
                                        text=""
                                        isChecked={!!s.enabled}
                                        onChange={e => onToggleSource(s, e.target.value)}
                                    />
                                    <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => onDeleteSource(s)}>
                                        {t('news.sources.delete')}
                                    </button>
                                </div>
                            </div>
                        ))}

                        <form className="row g-2 align-items-end mt-3" onSubmit={onAddSource}>
                            <div className="col-12 col-md-3">
                                <label className="form-label small text-muted" htmlFor="news-source-name">{t('news.sources.name')}</label>
                                <input
                                    id="news-source-name"
                                    className="form-control"
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            <div className="col-12 col-md-5">
                                <label className="form-label small text-muted" htmlFor="news-source-url">{t('news.sources.url')}</label>
                                <input
                                    id="news-source-url"
                                    className="form-control"
                                    type="url"
                                    required
                                    value={form.url}
                                    onChange={e => setForm(prev => ({ ...prev, url: e.target.value }))}
                                />
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="form-label small text-muted" htmlFor="news-source-type">{t('news.sources.type')}</label>
                                <select
                                    id="news-source-type"
                                    className="form-select"
                                    value={form.type}
                                    onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                                >
                                    <option value="RSS">RSS</option>
                                    <option value="YOUTUBE">YouTube</option>
                                </select>
                            </div>
                            <div className="col-6 col-md-2">
                                <button className="btn btn-gray-800 w-100" type="submit">{t('news.sources.add')}</button>
                            </div>
                        </form>
                        {sourceError && (
                            <div className="alert alert-danger py-2 px-3 small mt-3 mb-0" role="alert">{sourceError}</div>
                        )}
                    </div>
                </div>

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default News;
